import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { Calculator, DollarSign, Percent, TrendingUp, Target, ArrowRight } from 'lucide-react';
import { siteConfig } from '@/config/site';

const calculators = [
    {
        icon: DollarSign,
        title: 'Pip Calculator',
        description:
            'Calculate the value of a pip for any currency pair and position size. Understand exactly how much each pip movement is worth in your account currency.',
    },
    {
        icon: Percent,
        title: 'Margin Calculator',
        description:
            'Determine the required margin to open and maintain a position. Know your margin requirements before entering a trade to manage leverage effectively.',
    },
    {
        icon: TrendingUp,
        title: 'Profit/Loss Calculator',
        description:
            'Estimate potential profit or loss based on entry price, exit price, and position size. Plan your risk-reward ratio before placing any trade.',
    },
    {
        icon: Target,
        title: 'Position Size Calculator',
        description:
            'Calculate the optimal position size based on your account balance, risk tolerance, and stop-loss distance. Never risk more than you can afford.',
    },
];

export default function Calculators() {
    return (
        <AppLayout title={`Trading Calculators - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <Badge className="mb-6">Trading Tools</Badge>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Trading <span className="text-gradient">Calculators</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                            Essential calculators for every trader. Plan your trades, manage risk, and
                            optimize your position sizing with precision.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Calculator Cards */}
            <Section className="py-20 bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Choose Your"
                        highlight="Calculator"
                        description="Select from our suite of professional trading calculators."
                    />
                </FadeIn>
                <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {calculators.map((calc) => (
                        <motion.div key={calc.title} variants={staggerItem}>
                            <Card className="p-8 rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors h-full">
                                <CardContent className="p-0">
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-primary/10 border border-primary/10 mb-5">
                                        <calc.icon className="w-7 h-7 text-primary" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-foreground mb-3">
                                        {calc.title}
                                    </h3>
                                    <p className="text-muted-foreground mb-5">{calc.description}</p>
                                    <Badge variant="secondary">Coming Soon</Badge>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="py-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-2xl mx-auto">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 border border-primary/10 mb-6">
                            <Calculator className="w-8 h-8 text-primary" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Trade With <span className="text-gradient">Precision</span>
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Join {siteConfig.brand.name} and access all trading calculators alongside our full suite of tools.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Started <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </a>
                            <Link to="/tools/calculators">
                                <Button variant="outline" size="lg">Explore Tools</Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
